import type { HostLocale } from "@vidimo/shared";
import de from "../../messages/de.json";
import en from "../../messages/en.json";
import fr from "../../messages/fr.json";
import it from "../../messages/it.json";
import sl from "../../messages/sl.json";
import webOnlyDe from "../../messages/web-only/de.json";
import webOnlyEn from "../../messages/web-only/en.json";
import webOnlyFr from "../../messages/web-only/fr.json";
import webOnlyIt from "../../messages/web-only/it.json";
import webOnlySl from "../../messages/web-only/sl.json";

/** The shape every catalog is read as: English's, key for key. */
export type InterfaceMessages = typeof en & typeof webOnlyEn;

/**
 * English as this build ships it. The web-only catalog holds commerce copy the
 * native build must not carry (App Store Guideline 3.1.1); it joins only off
 * native, so the bundler drops it there and the native-bundle guard, which
 * greps for that copy, stays green.
 */
const ENGLISH: InterfaceMessages = __NATIVE_BUILD__
  ? (en as InterfaceMessages)
  : { ...en, ...webOnlyEn };

/**
 * Every host locale's catalog, in the entry bundle, so the first frame renders
 * in the host's language with nothing awaited and nothing to fetch offline.
 * The typecheck refuses a catalog that lacks one of English's keys: the web
 * build's as joined, the native build's before its cast claims the commerce
 * copy it never renders. `messages.test.ts` refuses the extra keys.
 */
const CATALOGS: Record<HostLocale, InterfaceMessages> = {
  // Each joins its web-only catalog inline, as ENGLISH does, so the bundler
  // folds the branch away on native and drops the commerce copy with it.
  de: __NATIVE_BUILD__
    ? (de satisfies typeof en as InterfaceMessages)
    : { ...de, ...webOnlyDe },
  en: ENGLISH,
  fr: __NATIVE_BUILD__
    ? (fr satisfies typeof en as InterfaceMessages)
    : { ...fr, ...webOnlyFr },
  it: __NATIVE_BUILD__
    ? (it satisfies typeof en as InterfaceMessages)
    : { ...it, ...webOnlyIt },
  sl: __NATIVE_BUILD__
    ? (sl satisfies typeof en as InterfaceMessages)
    : { ...sl, ...webOnlySl },
};

export const ENGLISH_MESSAGES: InterfaceMessages = ENGLISH;

export function interfaceMessages(locale: HostLocale): InterfaceMessages {
  return CATALOGS[locale];
}
